import React from 'react';
import Table from '../../components/common/Table';
import { Badge } from '../../components/common/Badge';

interface ComplianceRow {
    complianceYear: string;
    certificateType: 'Hunt' | 'Sport';
    completed: 'Completed' | 'Not completed';
    status: string;
    compliantTo: string;
    membershipFees: 'Up to date' | 'Life member' | 'Overdue';
    datePaidUpTo: string;
    activity1: 'Completed' | 'Uncompleted' | 'Not needed';
    activity2: 'Completed' | 'Uncompleted' | 'Not needed';
    daysLeft: string;
    howToFix: string;
}

interface ComplianceHistoryTableProps {
    data: ComplianceRow[];
    isLoading: boolean;
}

const ComplianceHistoryTable: React.FC<ComplianceHistoryTableProps> = ({
    data,
    isLoading,
}) => {
    const getStatusBadge = (status: string) => {
        switch (status.toLowerCase()) {
            case 'completed':
                return <Badge label="Completed" variant="success" />;
            case 'not completed':
            case 'uncompleted':
                return <Badge label="Not completed" variant="error" />;
            case 'not needed':
                return <Badge label="Not needed" variant="default" />;
            case 'up to date':
                return <Badge label="Up to date" variant="success" />;
            case 'life member':
                return <Badge label="Life member" variant="info" />;
            case 'overdue':
                return <Badge label="Overdue" variant="error" />;
            default:
                return <Badge label={status} variant="default" />;
        }
    };

    return (
        <Table
            columns={[
                {
                    label: 'Compliance Year',
                    key: 'complianceYear',
                    className: 'font-medium',
                },
                {
                    label: 'Status',
                    key: 'status',
                    render: (row: ComplianceRow) => getStatusBadge(row.status),
                },
                {
                    label: 'Compliant To',
                    key: 'compliantTo',
                    render: (row: ComplianceRow) => (
                        <span>{row.compliantTo || 'N/A'}</span>
                    ),
                },
                {
                    label: 'Membership Fees',
                    key: 'membershipFees',
                    render: (row: ComplianceRow) => getStatusBadge(row.membershipFees),
                },
                {
                    label: 'Date Paid Up To',
                    key: 'datePaidUpTo',
                },
                {
                    label: 'Activity 1',
                    key: 'activity1',
                    render: (row: ComplianceRow) => getStatusBadge(row.activity1),
                },
                {
                    label: 'Activity 2',
                    key: 'activity2',
                    render: (row: ComplianceRow) => getStatusBadge(row.activity2),
                },
                {
                    label: 'Days Left',
                    key: 'daysLeft',
                    render: (row: ComplianceRow) => {
                        const daysLeftText = row.daysLeft.trim();
                        const daysLeftValue = Number(daysLeftText);
                        const isNumericDays = daysLeftText !== '' && Number.isFinite(daysLeftValue);

                        if (!isNumericDays) {
                            return getStatusBadge(daysLeftText);
                        }

                        const isLowDays = daysLeftValue < 30;

                        return (
                            <span className={`font-medium ${isLowDays ? 'text-red-600' : 'text-gray-700'}`}>
                                {daysLeftText}
                            </span>
                        );
                    },
                },
                {
                    label: 'How to Fix',
                    key: 'howToFix',
                    className: 'max-w-xs',
                    render: (row: ComplianceRow) => {
                        const howToFix = row.howToFix;

                        if (howToFix.includes('Maintain status information')) {
                            const parts = howToFix.split('•');
                            return (
                                <span className="text-sm text-gray-600 break-words">
                                    {parts[0]}
                                    {parts.length > 1 && (
                                        <>
                                            • <a
                                                href="https://www.natshoot.co.za/dedicated-status/"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:text-blue-800 underline"
                                            >
                                                Maintain status information
                                            </a>
                                        </>
                                    )}
                                </span>
                            );
                        }

                        return (
                            <span className="text-sm text-gray-600 break-words">
                                {howToFix}
                            </span>
                        );
                    },
                },
            ]}
            data={data}
            isLoading={isLoading}
            allowPaging={false} // Compliance history typically shows all years
            noDataMessage="No compliance history available. Compliance history will appear here once activities are recorded."
        />
    );
};

export default ComplianceHistoryTable;